import EmptyState from '@rio-cloud/rio-uikit/EmptyState';
import ErrorState from '@rio-cloud/rio-uikit/ErrorState';
import ExpanderPanel from '@rio-cloud/rio-uikit/ExpanderPanel';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import { sortBy } from 'es-toolkit/compat';
import { type ReactNode, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect, useSelector } from 'react-redux';

import { getLocale } from '../../../../configuration';
import type { RootDispatch, RootState } from '../../../../configuration/setup/store';
import { getHasError, getIsLoading } from '../../api/redux/api.redux';
import { ApiCalls } from '../../api/redux/types';
import { DiscountInfoPanel } from '../../common/discount/DiscountInfoPanel';
import { fetchDiscountsThunk } from '../../common/discount/discounts.thunk';
import { getProductDiscounts } from '../../common/discount/redux/discount.redux';
import { isServiceVisible } from '../../common/utils/isServiceVisible';
import { getTenant, type Tenant } from '../../common/utils/tenants';
import { getLocalizedOpenMarketplaceLink } from '../../config/productConfig';
import { MaintenanceInfoBanner } from './components/MaintenanceInfoBanner';
import { ServiceTabHeader } from './components/ServiceTabHeader';
import { getServiceOverviewList } from './redux/serviceOverview.redux';
import { ServiceBodyContainer } from './ServiceBodyContainer';
import { ServiceHeaderContainer } from './ServiceHeaderContainer';
import { fetchActivationSummaryThunk } from './thunks/activationSummary.thunk';
import type { ServiceOverviewItem } from './types';

interface StateProps {
    serviceList: ServiceOverviewItem[];
    hasError: boolean;
    isLoading: boolean;
    locale: string;
    tenant: Tenant;
}

interface DispatchProps {
    fetchServiceList: () => void;
}

export type ServiceOverviewProps = StateProps & DispatchProps;

const FetchErrorMessage = () => (
    <ErrorState headline={<FormattedMessage id={'intl-msg:marketplace.myServices.fetch.error'} />} />
);

const EmptyMessage = (props: { locale: string; tenant: Tenant }) => (
    <EmptyState
        headline={<FormattedMessage id={'intl-msg:marketplace.myServices.empty.headline'} />}
        message={<FormattedMessage id={'intl-msg:marketplace.myServices.empty'} />}
        buttons={[
            {
                text: (
                    <span>
                        <span className='uikit-icon icon-shopping-cart' aria-hidden='true' />
                        <FormattedMessage id='intl-msg:marketplace.exploreServices' />
                    </span>
                ),
                href: getLocalizedOpenMarketplaceLink(props.locale),
            },
        ]}
        outerClassName='margin-top-15pct'
    />
);

const LoadingIndicator = () => (
    <div>
        <br />
        <Spinner text={<FormattedMessage id={'intl-msg:marketplace.myServices.loading'} />} isInverse={false} />
    </div>
);

const ServiceList = ({ serviceList }: Pick<ServiceOverviewProps, 'serviceList'>) => {
    const allProductDiscounts = useSelector(getProductDiscounts);

    if (!serviceList) {
        return null;
    }

    const sortedItems = sortBy(serviceList, 'productName');

    return (
        <div>
            {sortedItems
                .filter(service => isServiceVisible(service))
                .map(service => {
                    const productDiscount = allProductDiscounts.find(
                        pd => pd.productId.toLowerCase() === service.sku.toLowerCase()
                    );

                    return (
                        <ExpanderPanel
                            key={service.sku}
                            titleClassName={'width-100pct'}
                            title={<ServiceHeaderContainer service={service} discount={productDiscount} />}
                            bsStyle='default'
                            bodyClassName='padding-0 border-none rounded-bottom bg-lightest'
                            className='ServiceOverviewItem shadow-default'
                        >
                            <ServiceBodyContainer service={service} />
                        </ExpanderPanel>
                    );
                })}
        </div>
    );
};

export const ServiceOverview = (props: ServiceOverviewProps) => {
    useEffect(() => {
        props.fetchServiceList();
        // The effect should be like didMount and willUnmount => no deps
    }, []);

    let content: ReactNode;

    if (props.hasError) {
        content = <FetchErrorMessage />;
    } else if (props.isLoading) {
        content = <LoadingIndicator />;
    } else if (!props.serviceList.length) {
        return (
            <div className='ServiceOverview container-fluid fluid-default'>
                <DiscountInfoPanel />
                <EmptyMessage locale={props.locale} tenant={props.tenant} />
            </div>
        );
    } else {
        content = <ServiceList {...props} />;
    }

    return (
        <div className='ServiceOverview container-fluid fluid-default'>
            <ServiceTabHeader />
            <MaintenanceInfoBanner />
            <DiscountInfoPanel />
            {content}
        </div>
    );
};

export function mapStateToProps(state: RootState): StateProps {
    return {
        serviceList: getServiceOverviewList(state),
        hasError: getHasError(state, ApiCalls.ACTIVATION_SUMMARY),
        isLoading: getIsLoading(state, ApiCalls.ACTIVATION_SUMMARY),
        locale: getLocale(state),
        tenant: getTenant(state),
    };
}

export function mapDispatchToProps(dispatch: RootDispatch): DispatchProps {
    return {
        fetchServiceList: () => {
            dispatch(fetchActivationSummaryThunk(true));
            dispatch(fetchDiscountsThunk());
        },
    };
}

export const ServiceOverviewContainer = connect(mapStateToProps, mapDispatchToProps)(ServiceOverview);
